import { useState, useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import { useUserList, UserListInput } from "@/graphql/hooks/useUserList"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import {
  Search,
  Users,
  Filter,
  Edit,
  Mail,
  Phone,
  CreditCard,
  CheckCircle,
  TriangleAlert,
} from "lucide-react"
import { Skeleton } from "@/components/ui/skeleton"
import { UserEditModal } from "@/components/UserEditModal"
import Layout from "@/components/Layout/Layout"
import { useAuthContext } from "@/contexts/AuthContext"
import { LoadingScreen } from "@/components/ui/loading-spinner"
import { UserAvatar } from "@/components/ui/user-avatar"
import { calcularEdad } from "@/lib/utils"
import { CountryFlag } from "@/components/ui/country-flag"
import { useCountries, useRegions } from "@/graphql"

const UserManagement = () => {
  const { isAdmin, isAuthenticated } = useAuthContext()
  const [searchParams, setSearchParams] = useSearchParams()
  const [editingUser, setEditingUser] = useState(null)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [searchInput, setSearchInput] = useState(searchParams.get("search") || "")
  const [filters, setFilters] = useState<UserListInput>({
    search: searchParams.get("search") || "",
    countryId: parseInt(searchParams.get("countryId") || "0"),
    regionId: parseInt(searchParams.get("regionId") || "0"),
    active: parseInt(searchParams.get("active") || "1"),
    isValid: parseInt(searchParams.get("isValid") || "1"),
    page: parseInt(searchParams.get("page") || "1"),
    limit: 20,
  })

  // Sync searchInput when search filter changes from external sources
  useEffect(() => {
    if (filters.search !== searchInput) {
      setSearchInput(filters.search || "")
    }
  }, [filters.search])

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => ({
        ...prev,
        search: searchInput,
        page: 1, // Reset to page 1 when search changes
      }))
    }, 1500)

    return () => clearTimeout(timer)
  }, [searchInput])

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault()
      setFilters((prev) => ({
        ...prev,
        search: searchInput,
        page: 1,
      }))
    }
  }

  const { users, total, pages, loading, refetch } = useUserList(filters, {
    skip: !isAuthenticated || !isAdmin,
  })
  const { countries, countriesMap } = useCountries()
  const { regions, regionsMap } = useRegions(filters.countryId || 0)

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams()
    if (filters.search) params.set("search", filters.search)
    if (filters.countryId && filters.countryId > 0)
      params.set("countryId", filters.countryId.toString())
    if (filters.regionId && filters.regionId > 0)
      params.set("regionId", filters.regionId.toString())
    if (filters.active !== undefined) params.set("active", filters.active.toString())
    if (filters.isValid !== undefined) params.set("isValid", filters.isValid.toString())
    if (filters.page && filters.page > 1) params.set("page", filters.page.toString())

    setSearchParams(params)
  }, [filters, setSearchParams])

  const handleFilterChange = (key: keyof UserListInput, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: key === "page" ? value : 1, // Reset to page 1 when other filters change
    }))
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    refetch()
  }

  const getStatusBadge = (user) => {
    if (user.active === 2) {
      return <Badge variant="destructive">Baneado</Badge>
    }
    if (!user.emailValid) {
      return <Badge variant="info">Email no validado</Badge>
    }
    return <Badge variant="default">Activo</Badge>
  }

  const getValidationBadge = (isValid: number) => {
    switch (isValid) {
      case 1:
        return <Badge variant="default">Activos</Badge>
      case 2:
        return <Badge variant="destructive">Baneados</Badge>
      default:
        return <Badge variant="secondary">Todos</Badge>
    }
  }

  const handleEditUser = (user) => {
    setEditingUser(user)
    setIsEditModalOpen(true)
  }

  const handleEditSuccess = () => {
    refetch()
  }

  const renderPagination = () => {
    if (pages <= 1) return null

    const currentPage = filters.page || 1

    return (
      <div className="flex justify-center mt-6">
        <Pagination>
          <PaginationContent>
            {currentPage > 1 && (
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => handleFilterChange("page", currentPage - 1)}
                  className="cursor-pointer"
                />
              </PaginationItem>
            )}

            {Array.from({ length: Math.min(5, pages) }, (_, i) => {
              const pageNum = Math.max(1, currentPage - 2) + i
              if (pageNum > pages) return null

              return (
                <PaginationItem key={pageNum}>
                  <PaginationLink
                    onClick={() => handleFilterChange("page", pageNum)}
                    isActive={currentPage === pageNum}
                    className="cursor-pointer"
                  >
                    {pageNum}
                  </PaginationLink>
                </PaginationItem>
              )
            })}

            {currentPage < pages && (
              <PaginationItem>
                <PaginationNext
                  onClick={() => handleFilterChange("page", currentPage + 1)}
                  className="cursor-pointer"
                />
              </PaginationItem>
            )}
          </PaginationContent>
        </Pagination>
      </div>
    )
  }

  if (loading || !isAdmin || !isAuthenticated) {
    return <LoadingScreen />
  }

  return (
    <Layout>
      <div className="container mx-auto px-2 py-8">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-6 h-6" />
              Gestión de Usuarios
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4">
            {/* Filters */}
            <form onSubmit={handleSearch} className="space-y-4 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Buscar por nombre</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar usuario..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      onKeyDown={handleSearchKeyDown}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">País</label>
                  <Select
                    value={filters.countryId?.toString() || "0"}
                    onValueChange={(value) => handleFilterChange("countryId", parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar país" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Todos los países</SelectItem>
                      {countries.map((country) => (
                        <SelectItem key={country.id} value={country.id.toString()}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Región</label>
                  <Select
                    value={filters.regionId?.toString() || "0"}
                    onValueChange={(value) => handleFilterChange("regionId", parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar región" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Todas las regiones</SelectItem>
                      {regions.map((region) => (
                        <SelectItem key={region.id} value={region.id.toString()}>
                          {region.fullName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Estado</label>
                  <Select
                    value={filters.active?.toString() || "1"}
                    onValueChange={(value) => handleFilterChange("active", parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Estado del usuario" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">Todos</SelectItem>
                      <SelectItem value="1">Activos</SelectItem>
                      <SelectItem value="2">Sancionados / Baneados</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex gap-2">
                <Button type="submit" disabled={loading}>
                  <Filter className="w-4 h-4 mr-2" />
                  Filtrar
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setSearchInput("")
                    setFilters({
                      search: "",
                      countryId: 0,
                      regionId: 0,
                      active: 1,
                      isValid: 3,
                      page: 1,
                      limit: 20,
                    })
                  }}
                >
                  Limpiar
                </Button>
              </div>
            </form>

            {/* Results Info */}
            <div className="flex items-center justify-between mb-4 px-2">
              <p className="text-sm text-muted-foreground">Total: {total} usuarios</p>
              {getValidationBadge(filters.active || 3)}
            </div>

            {/* Table */}
            <div className="hidden md:block rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Usuario</TableHead>
                    <TableHead>Datos</TableHead>
                    <TableHead>Código</TableHead>
                    <TableHead>Categoría</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Registro</TableHead>
                    <TableHead>Edad</TableHead>
                    <TableHead className="w-20">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    // Loading skeleton
                    Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <div className="flex items-center space-x-3">
                            <Skeleton className="w-10 h-10 rounded-full" />
                            <div className="space-y-2">
                              <Skeleton className="h-4 w-32" />
                              <Skeleton className="h-3 w-24" />
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-40" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-16" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-20" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-32" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-6 w-16" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-4 w-20" />
                        </TableCell>
                        <TableCell>
                          <Skeleton className="h-8 w-8" />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : users.length > 0 ? (
                    users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <div className="flex items-center space-x-3">
                            <UserAvatar src={user.urlAvatar} name={user.fullName} size="lg" />
                            <div>
                              <p className="font-medium">{user.fullName}</p>
                              {user.Address && (
                                <p className="text-sm text-muted-foreground">
                                  <CountryFlag
                                    countryCode={
                                      countriesMap.get(user.Address.countryId).iso || "CL"
                                    }
                                  />
                                  {user.Address.Location?.name},{" "}
                                  {regionsMap.get(user.Address.regionId)?.name || "N/A"}
                                </p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm grid gap-2">
                          <p>
                            <Mail className="inline w-4 h-4 mr-2 text-muted-foreground" />{" "}
                            {user.email}
                          </p>
                          <p>
                            <Phone className="inline w-4 h-4 mr-2 text-muted-foreground" />{" "}
                            <Badge variant="outline" className="text-xs">
                              {user.phone ? user.phone : "Sin Teléfono"}
                            </Badge>
                          </p>
                          <p>
                            <CreditCard className="inline w-4 h-4 mr-2 text-muted-foreground" />{" "}
                            {user.documentNumber ? (
                              <Badge variant="outline" className="text-xs">
                                <CheckCircle className="inline text-green-600 h-4 mr-1" />
                                {user.documentNumber}
                              </Badge>
                            ) : (
                              <Badge variant="info" className="text-xs">
                                <TriangleAlert className="inline text-yellow-600 h-4 mr-1" />
                                Sin identificación
                              </Badge>
                            )}
                          </p>
                        </TableCell>
                        <TableCell>
                          <code className="bg-muted px-2 py-1 rounded text-xs">{user.code}</code>
                        </TableCell>
                        <TableCell>{user.PersonCategory?.name || "-"}</TableCell>
                        <TableCell>{getStatusBadge(user)}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {new Date(user.created).toLocaleDateString("es-ES")}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {calcularEdad(user.birthday)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditUser(user)}
                            className="h-8 w-8 p-0"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        No se encontraron usuarios
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Mobile: Cards */}
            <div className="md:hidden space-y-4">
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="p-4 border rounded-lg space-y-2">
                    <Skeleton className="h-6 w-32" />
                    <Skeleton className="h-4 w-48" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                ))
              ) : users.length > 0 ? (
                users.map((user) => (
                  <div key={user.id} className="p-4 border rounded-lg space-y-4">
                    {/* Header */}
                    <div className="flex items-center gap-3">
                      <UserAvatar src={user.urlAvatar} name={user.fullName} size="lg" />
                      <div className="min-w-0">
                        <p className="font-medium truncate">{user.fullName}</p>
                        {user.Address && (
                          <p className="text-sm text-muted-foreground truncate">
                            <CountryFlag
                              countryCode={countriesMap.get(user.Address.countryId)?.iso || "CL"}
                            />{" "}
                            {user.Address.Location?.name},{" "}
                            {regionsMap.get(user.Address.regionId)?.name || "N/A"}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Datos de contacto */}
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-muted-foreground shrink-0" />
                        <span className="truncate">{user.email}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-muted-foreground shrink-0" />
                        <Badge variant="outline" className="text-xs truncate">
                          {user.phone ? user.phone : "Sin Teléfono"}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-muted-foreground shrink-0" />
                        {user.documentNumber ? (
                          <Badge variant="outline" className="text-xs truncate">
                            <CheckCircle className="inline text-green-600 h-4 mr-1" />
                            {user.documentNumber}
                          </Badge>
                        ) : (
                          <Badge variant="info" className="text-xs truncate">
                            <TriangleAlert className="inline text-yellow-600 h-4 mr-1" />
                            Sin identificación
                          </Badge>
                        )}
                      </div>
                    </div>

                    {/* Extra info */}
                    <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                      <div>
                        <span className="block font-medium text-foreground">Código</span>
                        <code className="bg-muted px-2 py-0.5 rounded">{user.code}</code>
                      </div>
                      <div>
                        <span className="block font-medium text-foreground">Categoría</span>
                        <span>{user.PersonCategory?.name || "-"}</span>
                      </div>
                      <div>
                        <span className="block font-medium text-foreground">Estado</span>
                        {getStatusBadge(user)}
                      </div>
                      <div>
                        <span className="block font-medium text-foreground">Registro</span>
                        <span>{new Date(user.created).toLocaleDateString("es-ES")}</span>
                      </div>
                      <div>
                        <span className="block font-medium text-foreground">Edad</span>
                        <span>{calcularEdad(user.birthday)}</span>
                      </div>
                    </div>

                    {/* Acciones */}
                    <div className="flex justify-end pt-2 border-t">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditUser(user)}
                        className="h-8 w-8 p-0"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-center py-8 text-muted-foreground">No se encontraron usuarios</p>
              )}
            </div>

            {/* Pagination */}
            {renderPagination()}
          </CardContent>
        </Card>

        {/* Edit User Modal */}
        <UserEditModal
          user={editingUser}
          open={isEditModalOpen}
          onOpenChange={setIsEditModalOpen}
          onSuccess={handleEditSuccess}
        />
      </div>
    </Layout>
  )
}

export default UserManagement
